<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import MultiselectDropdown from '../../../../../shared/components/ui/MultiselectDropdown.vue';

const props = defineProps({
  slaPolicies: {
    type: Array,
    default: () => [],
  },
  selectedSlaPolicyId: {
    type: [Number, String],
    default: null,
  },
  ticketId: {
    type: [Number, String],
    required: true,
  },
  updateHistory: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  slaEvents: {
    type: Array,
    default: () => [],
  },
  ticketCreatedAt: {
    type: String,
    default: null,
  },
  slaDeadlines: {
    type: Object,
    default: () => ({}),
  },
  showTimeLimits: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:slaPolicyId', 'change']);

const { t } = useI18n();

const store = useStore();

const isSameId = (firstId, secondId) =>
  firstId !== null &&
  firstId !== undefined &&
  secondId !== null &&
  secondId !== undefined &&
  String(firstId) === String(secondId);

const selectedSlaPolicy = computed(() => {
  if (!props.selectedSlaPolicyId) return {};
  return (
    props.slaPolicies.find(policy =>
      isSameId(policy.id, props.selectedSlaPolicyId)
    ) || {}
  );
});

const formatTimeThreshold = seconds => {
  if (!seconds) return 'N/A';

  const days = Math.floor(seconds / 86400);
  const remainingSecondsAfterDays = seconds % 86400;
  const hours = Math.floor(remainingSecondsAfterDays / 3600);
  const remainingSecondsAfterHours = remainingSecondsAfterDays % 3600;
  const minutes = Math.floor(remainingSecondsAfterHours / 60);

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Reactive time for auto-update
const currentTime = ref(new Date());
let timeInterval = null;

onMounted(() => {
  // Update time every minute to refresh colors
  timeInterval = setInterval(() => {
    currentTime.value = new Date();
  }, 60000);
});

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval);
  }
});

// Get SLA event for a specific event type
const getSlaEvent = eventType => {
  if (!props.slaEvents || props.slaEvents.length === 0) return null;
  return props.slaEvents.find(event => event.event_type === eventType);
};

// Format deadline time limit
const formatDeadline = dateString => {
  if (!dateString) return null;
  const date = new Date(dateString);
  // Check if date is valid
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'HH:mm:ss, dd/MM/yyyy');
};

// Get deadline date object for an event type
const getDeadlineDate = (eventType, thresholdSeconds) => {
  if (!thresholdSeconds) return null;

  const event = getSlaEvent(eventType);

  // If event exists and has a deadline
  if (event && event.deadline_at) {
    const date = new Date(event.deadline_at);
    // Check if date is valid
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  // Prefer server-computed deadlines (especially for business hours).
  const serverDeadline = props.slaDeadlines?.[eventType];
  if (serverDeadline) {
    const date = new Date(serverDeadline);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  // RT events are only created after resolution, so for active tickets we don't have
  // a backend deadline. If the SLA counts only during business hours, a client-side
  // deadline (created_at + seconds) will be wrong, so we intentionally don't show it.
  if (
    eventType === 'rt' &&
    selectedSlaPolicy.value?.only_during_business_hours
  ) {
    return null;
  }

  if (eventType === 'nrt' && !props.slaDeadlines?.nrt) {
    return null;
  }

  if (props.ticketCreatedAt) {
    const ticketCreated = new Date(props.ticketCreatedAt);
    // Check if ticket creation date is valid
    if (Number.isNaN(ticketCreated.getTime())) return null;
    const thresholdMs = thresholdSeconds * 1000;
    const deadlineDate = new Date(ticketCreated.getTime() + thresholdMs);
    // Check if calculated date is valid
    if (Number.isNaN(deadlineDate.getTime())) return null;
    return deadlineDate;
  }

  return null;
};

// Get time limit (deadline) for an event type
const getTimeLimit = (eventType, thresholdSeconds) => {
  const deadlineDate = getDeadlineDate(eventType, thresholdSeconds);
  if (!deadlineDate) return null;
  // Check if date is valid before formatting
  if (Number.isNaN(deadlineDate.getTime())) return null;
  return formatDeadline(deadlineDate.toISOString());
};

// Calculate time progress percentage and get color class
const getTimeLimitColorClass = (eventType, thresholdSeconds) => {
  const deadlineDate = getDeadlineDate(eventType, thresholdSeconds);
  if (!deadlineDate) return 'text-n-slate-11';
  // Check if deadline date is valid
  if (Number.isNaN(deadlineDate.getTime())) return 'text-n-slate-11';

  const now = currentTime.value;
  const event = getSlaEvent(eventType);

  // Check if event is already completed
  let isCompleted = false;
  if (event) {
    if (event.event_type === 'rt' && event.meta?.resolved_at) {
      const resolvedDate = new Date(event.meta.resolved_at);
      if (!Number.isNaN(resolvedDate.getTime())) {
        isCompleted = resolvedDate <= now;
      }
    } else if (event.event_type === 'nrt' && event.meta?.next_response_at) {
      const nextResponseDate = new Date(event.meta.next_response_at);
      if (!Number.isNaN(nextResponseDate.getTime())) {
        isCompleted = nextResponseDate <= now;
      }
    } else if (event.event_type === 'frt' && event.meta?.first_response_at) {
      const firstResponseDate = new Date(event.meta.first_response_at);
      if (!Number.isNaN(firstResponseDate.getTime())) {
        isCompleted = firstResponseDate <= now;
      }
    }
  }

  if (isCompleted) {
    return 'text-green-600';
  }

  // Calculate start time (ticket creation or event creation)
  let startTime;
  if (event && event.created_at) {
    startTime = new Date(event.created_at);
    if (Number.isNaN(startTime.getTime())) return 'text-n-slate-11';
  } else if (props.ticketCreatedAt) {
    startTime = new Date(props.ticketCreatedAt);
    if (Number.isNaN(startTime.getTime())) return 'text-n-slate-11';
  } else {
    return 'text-n-slate-11';
  }

  const totalTime = deadlineDate - startTime;
  const remainingTime = deadlineDate - now;

  // If deadline has passed
  if (remainingTime <= 0) {
    return 'text-red-600 font-semibold';
  }

  // Calculate percentage of time remaining
  const percentageRemaining = (remainingTime / totalTime) * 100;

  // Green: more than 30% remaining (less than 70% of time has passed)
  if (percentageRemaining > 30) {
    return 'text-green-600';
  }

  // Yellow: 30% or less remaining (70% or more of time has passed)
  if (percentageRemaining > 0) {
    return 'text-yellow-600 font-medium';
  }

  return 'text-red-600 font-semibold';
};

// Computed properties for time limit
const firstResponseTimeLimit = computed(() =>
  getTimeLimit('frt', selectedSlaPolicy.value.first_response_time_threshold)
);

const nextResponseTimeLimit = computed(() =>
  getTimeLimit('nrt', selectedSlaPolicy.value.next_response_time_threshold)
);

const resolutionTimeLimit = computed(() =>
  getTimeLimit('rt', selectedSlaPolicy.value.resolution_time_threshold)
);

// Computed properties for color classes (reactive to currentTime)
const firstResponseColorClass = computed(() => {
  return getTimeLimitColorClass(
    'frt',
    selectedSlaPolicy.value.first_response_time_threshold
  );
});

const nextResponseColorClass = computed(() => {
  return getTimeLimitColorClass(
    'nrt',
    selectedSlaPolicy.value.next_response_time_threshold
  );
});

const resolutionColorClass = computed(() => {
  return getTimeLimitColorClass(
    'rt',
    selectedSlaPolicy.value.resolution_time_threshold
  );
});

const handleRemoveSlaPolicy = async () => {
  if (!props.updateHistory || !props.ticketId) return;

  try {
    const response = await store.dispatch('tickets/removeSlaPolicy', {
      id: props.ticketId,
    });

    if (response) {
      emit('update:slaPolicyId', null);
      emit('change', null);
    }

    // Refresh the ticket history immediately after the change
    await store.dispatch('tickets/getTicketHistory', props.ticketId);
  } catch (error) {
    // Error removing SLA - silently fail
  }
};

const handleSlaPolicyChange = async newSlaPolicy => {
  if (isSameId(newSlaPolicy.id, props.selectedSlaPolicyId)) {
    emit('update:slaPolicyId', null);
    emit('change', null);

    if (props.updateHistory && props.ticketId) {
      await handleRemoveSlaPolicy();
    }
    return;
  }

  emit('update:slaPolicyId', newSlaPolicy.id);
  emit('change', newSlaPolicy);

  if (props.updateHistory && props.ticketId) {
    try {
      const response = await store.dispatch('tickets/changeSlaPolicy', {
        id: props.ticketId,
        newSlaPolicy: newSlaPolicy.id,
      });

      if (response && response.sla_policy_name) {
        store.commit('tickets/UPDATE_TICKET_SLA_POLICY', {
          id: props.ticketId,
          sla_policy_name: response.sla_policy_name,
          sla_policy_id: response.sla_policy_id,
        });
      }

      await store.dispatch('tickets/getTicketHistory', props.ticketId);
    } catch (error) {
      // Error updating SLA policy - revert change
      emit('update:slaPolicyId', props.selectedSlaPolicyId);
    }
  }
};
</script>

<template>
  <div class="space-y-2">
    <div @click.prevent>
      <MultiselectDropdown
        :options="slaPolicies"
        :selected-item="selectedSlaPolicy"
        multiselector-title="Selecionar SLA"
        multiselector-placeholder="Selecionar SLA"
        input-placeholder="Buscar SLA..."
        no-search-result="Nenhum SLA encontrado"
        :has-thumbnail="false"
        :disabled="disabled"
        @select="handleSlaPolicyChange"
      />
    </div>

    <!-- SLA Policy Details -->
    <div
      v-if="selectedSlaPolicy && selectedSlaPolicy.id"
      class="mt-3 bg-n-solid-2 rounded-lg"
    >
      <div class="text-sm space-y-2">
        <div v-if="selectedSlaPolicy.description" class="text-n-slate-11">
          {{ selectedSlaPolicy.description }}
        </div>

        <div class="grid grid-cols-1 gap-2 text-xs">
          <table
            v-if="
              selectedSlaPolicy.first_response_time_threshold ||
              selectedSlaPolicy.next_response_time_threshold ||
              selectedSlaPolicy.resolution_time_threshold
            "
            class="w-full"
          >
            <thead>
              <tr>
                <th class="text-n-slate-11 text-left py-1">
                  {{ t('TICKETS.SLA.EVENT') }}
                </th>
                <th class="text-n-slate-11 text-left py-1">
                  {{ t('TICKETS.SLA.DEADLINE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-if="selectedSlaPolicy.first_response_time_threshold"
                class="text-xs"
              >
                <td class="text-n-slate-11 text-left py-1">
                  {{ t('TICKETS.SLA.FIRST_RESPONSE') }}{{ t('TICKETS.COLON') }}
                </td>
                <td class="text-n-slate-12 text-right py-1">
                  <span
                    v-if="
                      selectedSlaPolicy.first_response_time_threshold &&
                      !showTimeLimits
                    "
                    :class="firstResponseColorClass"
                  >
                    {{
                      formatTimeThreshold(
                        selectedSlaPolicy.first_response_time_threshold
                      )
                    }}
                  </span>
                  <span
                    v-if="firstResponseTimeLimit && showTimeLimits"
                    :class="firstResponseColorClass"
                  >
                    {{ firstResponseTimeLimit }}
                  </span>
                </td>
              </tr>

              <tr
                v-if="selectedSlaPolicy.next_response_time_threshold"
                class="text-xs"
              >
                <td class="text-n-slate-11 text-left py-1">
                  {{ t('TICKETS.SLA.NEXT_RESPONSE') }}{{ t('TICKETS.COLON') }}
                </td>
                <td class="text-n-slate-12 text-right py-1">
                  <span
                    v-if="
                      selectedSlaPolicy.next_response_time_threshold &&
                      !showTimeLimits
                    "
                    :class="nextResponseColorClass"
                  >
                    {{
                      formatTimeThreshold(
                        selectedSlaPolicy.next_response_time_threshold
                      )
                    }}
                  </span>
                  <span
                    v-if="nextResponseTimeLimit && showTimeLimits"
                    :class="nextResponseColorClass"
                  >
                    {{ nextResponseTimeLimit }}
                  </span>
                </td>
              </tr>

              <tr
                v-if="selectedSlaPolicy.resolution_time_threshold"
                class="text-xs"
              >
                <td class="text-n-slate-11 text-left py-1">
                  {{ t('TICKETS.SLA.RESOLUTION') }}{{ t('TICKETS.COLON') }}
                </td>
                <td class="text-n-slate-12 text-right py-1">
                  <span
                    v-if="resolutionTimeLimit && showTimeLimits"
                    :class="resolutionColorClass"
                  >
                    {{ resolutionTimeLimit }}
                  </span>
                  <span v-else :class="resolutionColorClass">
                    {{
                      formatTimeThreshold(
                        selectedSlaPolicy.resolution_time_threshold
                      )
                    }}
                  </span>
                </td>
              </tr>

              <tr class="text-xs">
                <td class="text-n-slate-11 text-left py-1">
                  {{ t('TICKETS.SLA.BUSINESS_HOURS') }}{{ t('TICKETS.COLON') }}
                </td>
                <td class="text-n-slate-12 text-right py-1">
                  {{
                    selectedSlaPolicy.only_during_business_hours
                      ? t('TICKETS.SLA.YES')
                      : t('TICKETS.SLA.NO')
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
